/**
 * First-touch campaign attribution for paid / organic landing URLs.
 *
 * Captures standard UTM params and ad click IDs from the initial page load,
 * stores them in sessionStorage (first touch wins for the tab session), and
 * merges them into GA4 funnel events via analytics.js.
 */

const STORAGE_KEY = 'fc:campaignAttribution';

const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'msclkid',
];

const safeSessionStorage = {
  get(key) {
    try {
      if (typeof window === 'undefined') return null;
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      if (typeof window === 'undefined') return;
      window.sessionStorage.setItem(key, value);
    } catch {
      /* private mode / disabled */
    }
  },
};

/**
 * Read attribution query params once per browser tab session.
 * Call as early as possible (src/index.js before React render).
 */
export function captureCampaignAttribution() {
  if (typeof window === 'undefined') return;

  if (safeSessionStorage.get(STORAGE_KEY)) return;

  const params = new URLSearchParams(window.location.search);
  const captured = {};

  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key);
    if (value) captured[key] = value;
  }

  if (Object.keys(captured).length > 0) {
    safeSessionStorage.set(STORAGE_KEY, JSON.stringify(captured));
  }
}

/** Params to merge into GA4 demo-funnel events for this session. */
export function getCampaignAttributionParams() {
  try {
    const raw = safeSessionStorage.get(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}
